// Puntuación especial (Formato): defaults = cómo cuenta hoy. El cálculo real
// de la tabla vive en el backend (standings/special_scoring.py); aquí solo
// hay edición de la config y el ejemplo en vivo.

import type { SpecialScoringConfig, StandingsPointsConfig, SuperTiebreakRange } from "./api";

export const DEFAULT_SPECIAL_SCORING: SpecialScoringConfig = {
  walkover_value: "one_game",
  walkover_score: null,
  defaulted_value: "same_as_walkover",
  defaulted_score: null,
  walkover_win_points: null,
  walkover_loss_points: null,
  bonus_bagel_set: 0,
  bonus_straight_sets: 0,
  bonus_set_in_loss: 0,
  exceptions_count_in_pcts: true,
  retired_mode: "keep",
  retired_score: null,
  super_tiebreak_value: "one_game",
  super_tiebreak_ranges: null,
};

export const DEFAULT_CHOSEN_SCORE = "6-0 6-0";
export const MAX_STB_RANGES = 10;

/** Ejemplo precargado de «Por rangos» (el organizador lo ajusta). */
export const EXAMPLE_STB_RANGES: SuperTiebreakRange[] = [
  { min: 0, max: 3, winner_games: 6, loser_games: 0 },
  { min: 4, max: 6, winner_games: 6, loser_games: 2 },
  { min: 7, max: 8, winner_games: 6, loser_games: 3 },
  { min: 9, max: null, winner_games: 6, loser_games: 4 },
];

export function normalizeSpecialScoring(value?: Partial<SpecialScoringConfig> | null): SpecialScoringConfig {
  return { ...DEFAULT_SPECIAL_SCORING, ...(value ?? {}) };
}

const KEYS = Object.keys(DEFAULT_SPECIAL_SCORING) as (keyof SpecialScoringConfig)[];

export function isDefaultSpecialScoring(value?: Partial<SpecialScoringConfig> | null): boolean {
  const v = normalizeSpecialScoring(value);
  return KEYS.every((key) => v[key] === DEFAULT_SPECIAL_SCORING[key]);
}

/** Parse "6-0 6-0" → [[6,0],[6,0]] (sin validar orden). */
export function parseChosenScore(text: string | null | undefined): [number, number][] {
  if (!text) return [];
  return Array.from(text.matchAll(/(\d+)\s*[-–:/]\s*(\d+)/g)).map((m) => [Number(m[1]), Number(m[2])] as [number, number]);
}

export function chosenScoreError(text: string | null | undefined): string | null {
  const sets = parseChosenScore(text);
  if (sets.length === 0) return "Escribe el marcador, ej. 6-0 6-0.";
  const won = sets.filter(([a, b]) => a > b).length;
  const lost = sets.filter(([a, b]) => b > a).length;
  return won > lost ? null : "El marcador debe ser a favor del ganador.";
}

const isInt = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n);

/** Misma validación que el backend: empieza en 0, sin huecos ni traslapes,
 * el último abierto («o más») y el ganador con más juegos. */
export function rangesError(ranges: SuperTiebreakRange[] | null | undefined): string | null {
  if (!ranges || ranges.length === 0) return "Agrega al menos un rango.";
  if (ranges.length > MAX_STB_RANGES) return `Máximo ${MAX_STB_RANGES} rangos.`;
  let expected = 0;
  for (let i = 0; i < ranges.length; i += 1) {
    const r = ranges[i];
    const n = i + 1;
    const last = i === ranges.length - 1;
    if (!isInt(r.min) || !isInt(r.winner_games) || !isInt(r.loser_games) || (!last && !isInt(r.max))) {
      return `Rango ${n}: completa todos los números.`;
    }
    if (r.min !== expected) return `Rango ${n}: debe empezar en ${expected} (sin huecos ni traslapes).`;
    if (r.max !== null && r.max < r.min) return `Rango ${n}: «a» no puede ser menor que «de».`;
    if (r.winner_games <= r.loser_games) return `Rango ${n}: el ganador debe tener más juegos que el perdedor.`;
    if (last && r.max !== null) return "El último rango debe quedar abierto («o más»).";
    expected = (r.max ?? 0) + 1;
  }
  return null;
}

export function specialScoringError(value: SpecialScoringConfig): string | null {
  if (value.walkover_value === "score") {
    const err = chosenScoreError(value.walkover_score);
    if (err) return `W.O.: ${err}`;
  }
  if (value.defaulted_value === "score") {
    const err = chosenScoreError(value.defaulted_score);
    if (err) return `DEF: ${err}`;
  }
  if (value.retired_mode === "score") {
    const err = chosenScoreError(value.retired_score);
    if (err) return `RET: ${err}`;
  }
  if (value.super_tiebreak_value === "ranges") {
    const err = rangesError(value.super_tiebreak_ranges);
    if (err) return `Super tiebreak: ${err}`;
  }
  return null;
}

function gamesOf(sets: [number, number][]): [number, number] {
  return sets.reduce<[number, number]>((acc, [a, b]) => [acc[0] + a, acc[1] + b], [0, 0]);
}

function rangeFor(ranges: SuperTiebreakRange[] | null | undefined, loserPoints: number): SuperTiebreakRange | null {
  return (ranges ?? []).find((r) => loserPoints >= r.min && (r.max === null || loserPoints <= r.max)) ?? null;
}

/** Cómo cuenta una victoria otorgada sin marcador (W.O. o DEF). */
function exceptionCredit(scoring: SpecialScoringConfig, defaulted: boolean): string {
  let mode: string = scoring.walkover_value;
  let score = scoring.walkover_score;
  if (defaulted && scoring.defaulted_value !== "same_as_walkover") {
    mode = scoring.defaulted_value;
    score = scoring.defaulted_score;
  }
  if (mode === "win_only") return "solo la victoria, sin sets ni juegos";
  if (!scoring.exceptions_count_in_pcts) return "no cuenta en los %";
  if (mode === "score") {
    const sets = parseChosenScore(score);
    return `${sets.filter(([a, b]) => a > b).length} sets, ${gamesOf(sets)[0]} juegos`;
  }
  return "0 sets, 1 juego";
}

/** Ejemplo en vivo: resultados contados con la config actual. */
export function specialScoringExample(
  scoring: SpecialScoringConfig,
  points: StandingsPointsConfig | null,
): { result: string; detail: string }[] {
  const pts = (n: number) => `${n} ${Math.abs(n) === 1 ? "pt" : "pts"}`;
  const rows: { result: string; detail: string }[] = [];

  // 1) Victoria 6-0 6-3
  const winSets: [number, number][] = [[6, 0], [6, 3]];
  const bagels = winSets.filter(([a, b]) => a === 6 && b === 0).length;
  if (points) {
    const bonus = bagels * scoring.bonus_bagel_set + scoring.bonus_straight_sets;
    rows.push({
      result: "Victoria 6-0 6-3",
      detail: bonus
        ? `${pts(points.win)} + ${bonus} bonus = ${pts(points.win + bonus)}`
        : pts(points.win),
    });
  } else {
    rows.push({ result: "Victoria 6-0 6-3", detail: "1 victoria · 2 sets, 12 juegos" });
  }

  // 2) Derrota 4-6 6-4 8-10 (super tiebreak, el perdedor anotó 8)
  let stbGames = 0;
  let stbNote = "super tiebreak = 1 juego";
  if (scoring.super_tiebreak_value === "points") {
    stbGames = 8;
    stbNote = "super tiebreak = sus puntos";
  } else if (scoring.super_tiebreak_value === "ranges") {
    const range = rangeFor(scoring.super_tiebreak_ranges, 8);
    stbGames = range ? range.loser_games : 0;
    stbNote = range ? `super tiebreak 8-10 = set ${range.loser_games}-${range.winner_games}` : "super tiebreak = 1 juego";
  }
  const lossGames = 4 + 6 + stbGames;
  const lossPts = points ? points.loss_completed + scoring.bonus_set_in_loss : null;
  rows.push({
    result: "Derrota 4-6 6-4 8-10",
    detail: `${lossPts !== null ? `${pts(lossPts)} · ` : ""}cuenta ${lossGames} juegos (${stbNote})`,
  });

  // 3) Victoria por W.O.
  const woWin = points ? (scoring.walkover_win_points ?? points.win) : null;
  rows.push({ result: "Victoria por W.O.", detail: `${woWin !== null ? `${pts(woWin)} · ` : ""}${exceptionCredit(scoring, false)}` });

  // 4) Victoria por DEF (solo si difiere del W.O.)
  if (scoring.defaulted_value !== "same_as_walkover") {
    rows.push({ result: "Victoria por DEF", detail: `${woWin !== null ? `${pts(woWin)} · ` : ""}${exceptionCredit(scoring, true)}` });
  }

  // 5) Derrota por W.O.
  const woLoss = points ? (scoring.walkover_loss_points ?? points.walkover_loss) : null;
  rows.push({ result: "Derrota por W.O.", detail: woLoss !== null ? pts(woLoss) : "derrota sin juegos" });

  // 6) Victoria 6-3 2-1 RET
  let retDetail = "cuenta 6-3 2-1: 1 set, 8 juegos";
  if (scoring.retired_mode === "complete") retDetail = "cuenta 6-3 6-1: 2 sets, 12 juegos";
  if (scoring.retired_mode === "score") {
    const sets = parseChosenScore(scoring.retired_score);
    retDetail = `cuenta ${sets.map(([a, b]) => `${a}-${b}`).join(" ")}: ${sets.filter(([a, b]) => a > b).length} sets, ${gamesOf(sets)[0]} juegos`;
  }
  if (scoring.retired_mode === "win_only") retDetail = "solo la victoria, sin sets ni juegos";
  rows.push({ result: "Victoria 6-3 2-1 RET", detail: `${points ? `${pts(points.win)} · ` : ""}${retDetail}` });
  return rows;
}

export function exampleHeadline(scoring: SpecialScoringConfig, points: StandingsPointsConfig | null): string {
  const parts: string[] = [];
  parts.push(points ? `puntos ${points.win}/${points.loss_completed}/${points.walkover_loss}` : "sin puntos");
  if (points && scoring.bonus_bagel_set) parts.push(`+${scoring.bonus_bagel_set} por set 6-0`);
  if (scoring.walkover_value === "score") parts.push(`W.O. = ${scoring.walkover_score ?? ""}`);
  if (scoring.walkover_value === "win_only") parts.push("W.O. solo victoria");
  if (scoring.defaulted_value === "win_only") parts.push("DEF solo victoria");
  if (scoring.retired_mode === "win_only") parts.push("RET solo victoria");
  if (scoring.retired_mode === "score") parts.push(`RET = ${scoring.retired_score ?? ""}`);
  if (scoring.super_tiebreak_value === "points") parts.push("super tiebreak con sus puntos");
  if (scoring.super_tiebreak_value === "ranges") parts.push("super tiebreak por rangos");
  return parts.join(" y ");
}
